/**
 * PC Dice Component
 *
 * Renders the retro computer-themed dice with Minecraft-font numbers (1-6).
 * The background structure (outer shell, face gradient, overlay borders) is shared
 * across all face values; only the displayed number changes per value.
 *
 * SVG structure extracted from Figma exports (Dice_PC_B_*.svg reference).
 * Reuses useDiceViewModel for shared animation/interaction logic.
 */

import React from 'react';
import { motion } from 'framer-motion';
import type { DiceProps } from '../basics/constants/types';
import type { PcDiceColorScheme } from './constants/diceColorSchemes';
import { useDiceViewModel } from '../basics/hooks/useDiceViewModel';
import { GRASS_TEXTURE_BASE64, DICE_CONFIG } from '../basics/utils/utils';
import '../basics/Dice.css';

interface PcDiceProps extends DiceProps {
  colorScheme: PcDiceColorScheme;
}

/**
 * X-translate positions for each face number (1-6), extracted from SVG exports.
 * Y position is always 123.04.
 */
const NUMBER_TRANSLATE_X: Record<number, number> = {
  1: 77.38,
  2: 72.1,
  3: 66.77,
  4: 67.36,
  5: 66.2,
  6: 69.15
};

const NUMBER_TRANSLATE_Y = 123.04;

const PcDiceComponent = React.memo(function PcDiceComponent({
  colorScheme,
  ...diceProps
}: PcDiceProps) {
  const {
    showDiceFace = true,
    showGround = true,
    isRolling = false,
    showGrassTexture = true,
    viewBox = '0 0 187 198'
  } = diceProps;

  const { state, actions } = useDiceViewModel(diceProps);

  const getId = state.getId;
  const translateX = NUMBER_TRANSLATE_X[state.displayedValue] ?? NUMBER_TRANSLATE_X[1];

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={viewBox}
      className={state.svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      role="img"
      aria-label={`Dice showing ${String(state.validValue)}`}
      aria-live={isRolling ? 'polite' : 'off'}
      focusable="false"
      onClick={actions.handleClick}
      style={{ cursor: state.cursorStyle }}
    >
      <defs>
        {/* Drop shadow filter */}
        <filter id={getId('dropShadow')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation={DICE_CONFIG.SHADOW_3D.BLUR_RADIUS} />
          <feOffset
            dx={DICE_CONFIG.SHADOW_3D.OFFSET_X}
            dy={DICE_CONFIG.SHADOW_3D.OFFSET_Y}
            result="offsetblur"
          />
          <feComponentTransfer>
            <feFuncA type="linear" slope={DICE_CONFIG.SHADOW_3D.OPACITY} />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Main face horizontal gradient (left-to-right) */}
        <linearGradient
          id={getId('faceGrad')}
          x1="30.89"
          y1="89.44"
          x2="154.53"
          y2="89.44"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={colorScheme.faceGradient[0]} />
          <stop offset=".27" stopColor={colorScheme.faceGradient[1]} />
          <stop offset=".48" stopColor={colorScheme.faceGradient[2]} />
          <stop offset=".67" stopColor={colorScheme.faceGradient[3]} />
          <stop offset=".84" stopColor={colorScheme.faceGradient[4]} />
          <stop offset="1" stopColor={colorScheme.faceGradient[5]} />
        </linearGradient>

        {/* Vertical overlay gradient (border highlight at top/bottom edges) */}
        <linearGradient
          id={getId('overlayVert')}
          x1="92.71"
          y1="25.35"
          x2="92.71"
          y2="153.78"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#b3b3b3" />
          <stop offset=".08" stopColor={colorScheme.overlayMidColor} stopOpacity="0" />
          <stop offset=".92" stopColor={colorScheme.overlayMidColor} stopOpacity="0" />
          <stop offset="1" stopColor="#b3b3b3" />
        </linearGradient>

        {/* Horizontal overlay gradient (border highlight at left/right edges) */}
        <linearGradient
          id={getId('overlayHoriz')}
          x1="92.71"
          y1="25.35"
          x2="92.71"
          y2="153.78"
          gradientUnits="userSpaceOnUse"
          gradientTransform="rotate(90, 92.71, 89.565)"
        >
          <stop offset="0" stopColor="#b3b3b3" />
          <stop offset=".08" stopColor={colorScheme.overlayMidColor} stopOpacity="0" />
          <stop offset=".92" stopColor={colorScheme.overlayMidColor} stopOpacity="0" />
          <stop offset="1" stopColor="#b3b3b3" />
        </linearGradient>

        {/* Grass texture */}
        {showGrassTexture && (
          <pattern
            id={getId('texture')}
            patternContentUnits="objectBoundingBox"
            width="1"
            height="1"
          >
            <use xlinkHref={`#${getId('textureImg')}`} transform="scale(0.00436681)" />
          </pattern>
        )}
        {showGrassTexture && (
          <image
            id={getId('textureImg')}
            width="229"
            height="228"
            preserveAspectRatio="none"
            xlinkHref={GRASS_TEXTURE_BASE64}
          />
        )}
      </defs>

      {/* Fixed ground/background (retro shell + casing) — only rendered when showGround */}
      {showGround && (
        <>
          {/* Outer shell — tan/beige bottom ledge (3D depth effect) */}
          <path
            fill="#bab7a8"
            d="M15.2,136.34v25.43c0,7.44,2.08,14.39,5.49,17.91.03.03.05.06.08.08,3.96,4.02,7.7,3.09,12.22,2.69,5.51-.49,11.02,1.06,16.53,1.06,10.99,0,21.97.03,32.96,0,7.9-.02,15.67,0,21.24,0,8.52.01,12.78.02,13.22.04,18.72.56,20.21,2.99,26.85,0,4.59-2.07,6.23-4.29,10.52-4.17,4.06.11,6.66,2.21,10.02-.38,2.74-2.12,4.71-7.11,4.71-7.11,0-.01.01-.02.02-.04.8-1.77,1.21-4.06,1.2-6.4-.06-11.05-.11-22.1-.17-33.14-24.75,7.13-53.49,12.24-85.48,12.2-25.43-.03-48.7-3.3-69.4-8.17Z"
          />

          {/* Inner cream ledge face */}
          <path
            fill="#f9f4d9"
            d="M15.18,127.07v33.56c1.06,3.02,2.83,7.08,5.58,9.86,3.96,4.02,7.7,3.09,12.22,2.69,5.51-.49,11.02,1.06,16.53,1.06,10.99,0,21.97.03,32.96,0,7.9-.02,15.67,0,21.24,0,8.52.01,12.78.02,13.22.04,18.72.56,20.21,2.99,26.84,0,4.59-2.07,6.23-4.29,10.52-4.17,4.06.11,6.66,2.21,10.02-.38,2.74-2.12,4.71-7.11,4.71-7.11.55-1.22.96-2.33,1.24-3.14-.06-12.15-.12-24.3-.18-36.44-24.75,7.13-53.49,12.24-85.48,12.2-25.43-.03-48.7-3.3-69.4-8.17Z"
          />

          {/* Rounded rectangle outer casing (gray-green) */}
          <rect
            fill="#798c83"
            x="15.49"
            y="12.32"
            width="154.75"
            height="154.75"
            rx="19.75"
            ry="19.75"
          />

          {/* Grass/noise texture overlay on casing */}
          {showGrassTexture && (
            <image
              width="229"
              height="228"
              transform="translate(-21.62 -24.32)"
              xlinkHref={GRASS_TEXTURE_BASE64}
            />
          )}
        </>
      )}

      {/* Animated face group — face gradient + overlay borders + Minecraft number */}
      {showDiceFace && (
        <motion.g
          animate={state.combinedAnimation}
          className="dice-3d-transform"
          style={{
            transformOrigin: '92.71px 89.44px',
            opacity: isRolling ? DICE_CONFIG.INACTIVE_OPACITY : 1
          }}
          filter={`url(#${getId('dropShadow')})`}
        >
          {/* Main colored face */}
          <rect
            fill={`url(#${getId('faceGrad')})`}
            x="30.89"
            y="28.04"
            width="123.65"
            height="122.8"
            rx="19.75"
            ry="19.75"
          />

          {/* Vertical edge overlay */}
          <rect
            fill={`url(#${getId('overlayVert')})`}
            x="30.89"
            y="28.04"
            width="123.65"
            height="122.8"
            rx="19.75"
            ry="19.75"
          />

          {/* Horizontal edge overlay */}
          <rect
            fill={`url(#${getId('overlayHoriz')})`}
            x="31.31"
            y="28"
            width="122.8"
            height="123.65"
            rx="19.75"
            ry="19.75"
          />

          {/* Minecraft-font number */}
          <text
            fill={colorScheme.numberColor}
            fontFamily="Minecraft, Minecraft"
            fontSize="94.46"
            transform={`translate(${String(translateX)} ${String(NUMBER_TRANSLATE_Y)})`}
          >
            <tspan x="0" y="0">
              {state.displayedValue}
            </tspan>
          </text>
        </motion.g>
      )}
    </svg>
  );
});

export default PcDiceComponent;
